import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fastapi } from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faCheckCircle, faTimesCircle, faFileInvoiceDollar, faRobot, faTimes, faKey, faBoxOpen, faShoppingCart, faTruck, faFlask, faTags, faPlug, faClinicMedical, faEnvelope, faTrashAlt, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { faStripe } from '@fortawesome/free-brands-svg-icons';

const ToggleSwitch: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
  <div
    role="switch"
    tabIndex={0}
    onKeyDown={event => { if (event.key === " " || event.key === "Enter") { event.preventDefault(); onChange(); } }}
    aria-checked={checked}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
      checked ? 'bg-pink-500' : 'bg-white/20'
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </div>
);

const AdminComerciosPage: React.FC = () => {
  const [comercios, setComercios] = useState<any[]>([]);
  const [features, setFeatures] = useState<Record<string, Record<string, boolean>>>({});
  const [selectedCommerce, setSelectedCommerce] = useState<any | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'deleted'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchComercios();
  }, []);

  const fetchComercios = async () => {
    try {
      const res = await fastapi.get('/admin/comercios');
      const comerciosData = res.data;
      setComercios(comerciosData);

      const featureMap: Record<string, Record<string, boolean>> = {};
      await Promise.all(
        comerciosData.map(async (c: any) => {
          try {
            const fres = await fastapi.get(`/admin/comercios/${c.comercio_key}/features`);
            const cFeatures: Record<string, boolean> = {};
            fres.data.forEach((f: any) => {
              cFeatures[f.feature_name] = f.is_enabled;
            });
            featureMap[c.comercio_key] = cFeatures;
          } catch (e) {
            console.error(e);
          }
        })
      );
      setFeatures(featureMap);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleFeature = async (comercioKey: string, featureName: string) => {
    const isEnabled = features[comercioKey]?.[featureName] || false;
    const newStatus = !isEnabled;

    try {
      setFeatures(prev => ({
        ...prev,
        [comercioKey]: {
          ...(prev[comercioKey] || {}),
          [featureName]: newStatus
        }
      }));

      await fastapi.patch(`/admin/comercios/${comercioKey}/features`, {
        feature_name: featureName,
        is_enabled: newStatus
      });
    } catch (e) {
      console.error(e);
      setFeatures(prev => ({
        ...prev,
        [comercioKey]: {
          ...(prev[comercioKey] || {}),
          [featureName]: isEnabled
        }
      }));
    }
  };

  const toggleDemoAccess = async (commerce: any) => {
    const allowed = !commerce.demo_allowed;
    setSelectedCommerce((current: any) => current ? { ...current, demo_allowed: allowed, demo_expires_at: allowed ? current.demo_expires_at : null } : current);
    setComercios(current => current.map(item => item.comercio_key === commerce.comercio_key
      ? { ...item, demo_allowed: allowed, demo_expires_at: allowed ? item.demo_expires_at : null }
      : item));
    try {
      await fastapi.patch(`/admin/comercios/${commerce.comercio_key}/demo-access`, { allowed });
    } catch (error) {
      console.error(error);
      await fetchComercios();
      setSelectedCommerce(commerce);
    }
  };

  const filteredComercios = comercios.filter((c) => {
    if (filterStatus === 'active') return c.is_active;
    if (filterStatus === 'deleted') return !c.is_active;
    return true;
  });

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-8 space-y-8 animate-fade-in-up">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-white">
            Tenant Management
          </h1>
          <p className="text-white/50 mt-1">Control access, features, and active/deleted status per commerce.</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center bg-white/5 border border-white/10 p-1.5 rounded-2xl backdrop-blur-xl">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              filterStatus === 'all'
                ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            Todos ({comercios.length})
          </button>
          <button
            onClick={() => setFilterStatus('active')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              filterStatus === 'active'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30 shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            Activos ({comercios.filter(c => c.is_active).length})
          </button>
          <button
            onClick={() => setFilterStatus('deleted')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              filterStatus === 'deleted'
                ? 'bg-red-500/20 text-red-400 border border-red-500/30 shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            Borrados ({comercios.filter(c => !c.is_active).length})
          </button>
        </div>
      </header>

      <div className="bg-black/20 border border-white/10 rounded-3xl backdrop-blur-xl overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-white/80 whitespace-nowrap">
            <thead className="bg-white/5 text-white/60 uppercase font-semibold border-b border-white/10">
              <tr>
                <th className="px-6 py-5">Tenant Info</th>
                <th className="px-6 py-5">Correo</th>
                <th className="px-6 py-5 text-center">Status</th>
                <th className="px-6 py-5">Fechas</th>
                <th className="px-6 py-5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredComercios.map((c) => (
                <tr key={c.id} className="hover:bg-white/5 transition-colors group">
                  <td className="px-6 py-5">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 border ${
                        c.is_active 
                          ? 'bg-gradient-to-br from-pink-500/20 to-purple-500/20 border-white/10' 
                          : 'bg-red-500/10 border-red-500/20'
                      }`}>
                        <FontAwesomeIcon icon={faStore} className={c.is_active ? 'text-white/70' : 'text-red-400'} />
                      </div>
                      <div>
                        <p className="font-semibold text-white text-base">{c.name}</p>
                        <p className="text-xs text-white/40 font-mono mt-0.5">{c.comercio_key}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-white/80">
                    {c.email ? (
                      <span className="font-mono text-sm">{c.email}</span>
                    ) : (
                      <span className="text-white/30 italic text-xs">Sin correo</span>
                    )}
                  </td>
                  <td className="px-6 py-5 text-center">
                    {c.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1.5" /> Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                        <FontAwesomeIcon icon={faTimesCircle} className="mr-1.5" /> Eliminado
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-5 text-xs text-white/60">
                    <div>
                      <span className="text-white/30">Creado:</span> {formatDate(c.created_at)}
                    </div>
                    {c.deleted_at && (
                      <div className="text-red-400 mt-0.5">
                        <span className="text-red-400/50">Borrado:</span> {formatDate(c.deleted_at)}
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-5 text-right space-x-3">
                    <button 
                      onClick={() => navigate(`/admin/comercios/${c.comercio_key}/chats`)}
                      className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      View Chats
                    </button>
                    <button 
                      onClick={() => navigate(`/admin/comercios/${c.comercio_key}/billing`)}
                      className="text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      View Billing
                    </button>
                    <button 
                      onClick={() => setSelectedCommerce(c)}
                      className="text-sm font-medium text-pink-400 hover:text-pink-300 transition-colors"
                    >
                      Detalles
                    </button>
                  </td>
                </tr>
              ))}
              {filteredComercios.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-white/50">
                    No se encontraron comercios para el filtro seleccionado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {selectedCommerce && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
            onClick={() => setSelectedCommerce(null)}
          ></div>
          <div className="relative bg-taratic-surface border border-white/10 p-8 rounded-3xl shadow-2xl max-w-lg w-full transform transition-all animate-fade-in-up">
            <button 
              onClick={() => setSelectedCommerce(null)}
              className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xl" />
            </button>
            
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border ${
                selectedCommerce.is_active 
                  ? 'bg-gradient-to-br from-pink-500/20 to-purple-500/20 border-white/10' 
                  : 'bg-red-500/10 border-red-500/20'
              }`}>
                <FontAwesomeIcon icon={faStore} className={`text-2xl ${selectedCommerce.is_active ? 'text-white/70' : 'text-red-400'}`} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{selectedCommerce.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${selectedCommerce.is_active ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                    {selectedCommerce.is_active ? 'Activo' : 'Eliminado'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
              {!selectedCommerce.is_active && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl space-y-2 text-xs">
                  <p className="font-semibold text-red-400 flex items-center gap-2 text-sm">
                    <FontAwesomeIcon icon={faTrashAlt} /> Comercio Eliminado
                  </p>
                  <p className="text-white/80">
                    <strong>Fecha de borrado:</strong> {formatDate(selectedCommerce.deleted_at)}
                  </p>
                  {selectedCommerce.deletion_reason && (
                    <p className="text-white/80">
                      <strong>Motivo:</strong> {selectedCommerce.deletion_reason}
                    </p>
                  )}
                  {selectedCommerce.deletion_comments && (
                    <div className="bg-black/30 p-2.5 rounded-lg border border-red-500/20 mt-1">
                      <p className="text-white/50 text-[11px] mb-1 font-semibold">Comentarios Extras:</p>
                      <p className="text-white/90 italic">{selectedCommerce.deletion_comments}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                <p className="text-sm text-white/50 mb-1 flex items-center gap-2">
                  <FontAwesomeIcon icon={faEnvelope} /> Correo Electrónico
                </p>
                <p className="font-mono text-white/90 break-all">{selectedCommerce.email || 'Sin correo asociado'}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                <p className="text-sm text-white/50 mb-1 flex items-center gap-2">
                  <FontAwesomeIcon icon={faKey} /> Commerce Key
                </p>
                <p className="font-mono text-white/90 break-all">{selectedCommerce.comercio_key}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                <p className="text-sm text-white/50 mb-1 flex items-center gap-2">
                  <FontAwesomeIcon icon={faCalendarAlt} /> Fecha de Creación
                </p>
                <p className="font-mono text-white/90">{formatDate(selectedCommerce.created_at)}</p>
              </div>

              {/* MODULES MANAGEMENT */}
              <div className="p-4 bg-white/5 rounded-xl border border-white/5 mt-4">
                <p className="text-sm text-white/50 mb-3 font-semibold uppercase tracking-wider">
                  Módulos y Funcionalidades
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'meta_limit_notifications', label: 'Mostrar avisos de límites Meta al operador', icon: faEnvelope },
                    { key: 'products', label: 'Productos', icon: faBoxOpen },
                    { key: 'orders', label: 'Pedidos', icon: faShoppingCart },
                    { key: 'delivery_settings', label: 'Delivery', icon: faTruck },
                    { key: 'test_api', label: 'Test API', icon: faFlask },
                    { key: 'chatbots', label: 'Bots & Stats', icon: faRobot },
                    { key: 'tags', label: 'Etiquetas', icon: faTags },
                    { key: 'connections', label: 'Conexiones', icon: faPlug },
                    { key: 'stripe', label: 'Stripe', icon: faStripe },
                    { key: 'clinics', label: 'Clínica', icon: faClinicMedical },
                    { key: 'invoices', label: 'Facturación', icon: faFileInvoiceDollar }
                  ].map((feat) => (
                    <div key={feat.key} className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-white/5">
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={feat.icon as any} className={features[selectedCommerce.comercio_key]?.[feat.key] ? 'text-pink-400 drop-shadow-[0_0_8px_rgba(236,72,153,0.8)]' : 'text-white/20'} />
                        <span className="text-sm text-white/90">{feat.label}</span>
                      </div>
                      <ToggleSwitch
                        checked={features[selectedCommerce.comercio_key]?.[feat.key] || false}
                        onChange={() => toggleFeature(selectedCommerce.comercio_key, feat.key)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-cyan-500/10 rounded-xl border border-cyan-400/20 mt-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={faFlask} className={selectedCommerce.demo_allowed ? 'text-cyan-300 mt-1' : 'text-white/30 mt-1'} />
                    <div>
                      <p className="text-sm font-semibold text-white">Allow clinic demo</p>
                      <p className="text-xs text-white/55 mt-1">Lets this tenant start isolated, browser-local 10-minute demo sessions. It never changes billing or live clinic data.</p>
                      {selectedCommerce.demo_expires_at && (
                        <p className="text-xs text-cyan-200 mt-2">Current session expires: {formatDate(selectedCommerce.demo_expires_at)}</p>
                      )}
                    </div>
                  </div>
                  <ToggleSwitch checked={!!selectedCommerce.demo_allowed} onChange={() => toggleDemoAccess(selectedCommerce)} />
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button 
                onClick={() => setSelectedCommerce(null)}
                className="px-6 py-2.5 rounded-xl bg-white/10 text-white font-semibold hover:bg-white/20 transition-all shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminComerciosPage;
